"use client";

import { useRef } from "react";
import useSWR, { mutate as mutateSWRCache } from "swr";

/**
 * SWR-cached list-items fetch shared by the picker modals (gl-picker,
 * list-item-picker, vendor-picker) and the matching-tab cache warmer.
 *
 * The key is byte-identical to the URLs the pickers previously built by
 * hand — Task 3.1's route/RPC tests depend on the query params, and the
 * page.tsx `preload()` keys must match these for cache hits.
 */

/** Slug of the system GL accounts list (gl-picker + matching-tab warmer). */
export const GL_ACCOUNTS_LIST_SLUG = "gl_accounts";

/** Shape of one item as returned by GET /api/lists/[id]/items. */
export interface ListItemRecord {
  id: string;
  data: Record<string, unknown> | null;
  erpId?: string;
  aliases?: string[];
  source?: string;
  [key: string]: unknown;
}

interface ListItemsResponse {
  items?: ListItemRecord[];
}

export type ListItemsBatchResponse = Record<string, ListItemRecord[]>;

type ListItemsCacheSeed = (
  key: string,
  data: ListItemsResponse,
  options: { revalidate: false }
) => Promise<unknown> | unknown;

export interface UseListItemsOptions {
  /** List definition id (from useListBySlug). null → no fetch. */
  listId: string | null;
  /** Raw search term; trimmed before keying. Debounce BEFORE passing in. */
  search?: string;
  /** Dependent-field parent filter — both key and value required to apply. */
  parentKey?: string;
  parentValue?: string;
  /** Page size. Pickers use 100 except vendor-picker (50). */
  limit?: number;
  /** Maps from the picker's isOpen — false → null key, no fetch. */
  enabled?: boolean;
  /**
   * Admin-only "management" read: appends &management=true so the items route
   * returns hidden subsidiaries (route.ts gates this on user.role === "admin").
   * Off by default — omitting it keeps every existing picker key byte-identical.
   */
  management?: boolean;
  /**
   * Server-side equality scope on `erp_connection_id` (e.g. the
   * vendor-prepaid GL picker scoping accounts to one vendor's connection).
   * Omitted → byte-identical to every existing key.
   */
  erpConnectionId?: string;
}

export const LIST_ITEMS_SWR_CONFIG = {
  revalidateOnFocus: false,
  // Reopening a picker within 30s renders instantly from cache with no refetch.
  dedupingInterval: 30_000,
  // Keep the previous results visible while a new search key loads instead of
  // flashing a spinner on every debounced keystroke.
  keepPreviousData: true,
} as const;

export function buildListItemsKey({
  listId,
  search,
  parentKey,
  parentValue,
  limit = 100,
  enabled = true,
  management = false,
  erpConnectionId,
}: UseListItemsOptions): string | null {
  if (!enabled || !listId) return null;
  const normalizedSearch = (search ?? "").trim();
  const searchParam = normalizedSearch
    ? `&search=${encodeURIComponent(normalizedSearch)}`
    : "";
  const parentParam = parentKey && parentValue
    ? `&filter_parent_key=${encodeURIComponent(parentKey)}&filter_parent_value=${encodeURIComponent(parentValue)}`
    : "";
  // Appended LAST (both of them) so a key with neither is byte-identical to
  // the historical URL every existing caller relies on.
  const managementParam = management ? "&management=true" : "";
  const erpConnectionParam = erpConnectionId
    ? `&erp_connection_id=${encodeURIComponent(erpConnectionId)}`
    : "";
  return `/api/lists/${listId}/items?limit=${limit}&include_counts=false${searchParam}${parentParam}${managementParam}${erpConnectionParam}`;
}

/**
 * Merge by-id backfills (current/suggested item fetched outside the first
 * page) into a fetched result set. Backfills are pinned to the top and
 * persist across search terms until the dialog reopens — a deliberate change
 * from the old picker code, which dropped them whenever a search re-fetch
 * replaced the array. Fetched rows win on id collision (fresher data).
 */
export function mergeBackfilledItems<T extends { id: string }>(
  backfilled: T[],
  fetched: T[]
): T[] {
  const extras = backfilled.filter(
    (extra) => !fetched.some((item) => item.id === extra.id)
  );
  return [...extras, ...fetched];
}

/** Seed the exact SWR keys consumed by existing pickers. */
export async function seedListItemsBatchCache(
  batch: ListItemsBatchResponse,
  perListLimit = 100,
  seed: ListItemsCacheSeed = mutateSWRCache
): Promise<void> {
  await Promise.all(
    Object.entries(batch).map(([listId, items]) => {
      const key = buildListItemsKey({ listId, limit: perListLimit });
      if (!key) return undefined;
      return seed(key, { items }, { revalidate: false });
    })
  );
}

/**
 * Fetch one bounded batch and populate the existing per-list picker caches.
 * Phase 5 owns when this is scheduled; this helper only defines the request
 * and cache-seeding contract.
 */
export async function fetchAndSeedListItemsBatch({
  listDefinitionIds,
  perListLimit = 100,
  request = fetch,
  seed = mutateSWRCache,
}: {
  listDefinitionIds: string[];
  perListLimit?: number;
  request?: typeof fetch;
  seed?: ListItemsCacheSeed;
}): Promise<ListItemsBatchResponse> {
  const response = await request("/api/list-items/batch", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ listDefinitionIds, perListLimit }),
  });
  if (!response.ok) {
    throw new Error("Failed to warm list-item caches");
  }

  const batch = await response.json() as ListItemsBatchResponse;
  await seedListItemsBatchCache(batch, perListLimit, seed);
  return batch;
}

const EMPTY_ITEMS: ListItemRecord[] = [];

export function useListItems(options: UseListItemsOptions) {
  const key = buildListItemsKey(options);
  const { data, error, isLoading, mutate } = useSWR<ListItemsResponse>(
    key,
    LIST_ITEMS_SWR_CONFIG
  );

  // keepPreviousData lags the last key's payload while a new key loads. That
  // is wanted across search refinements of the SAME list, but must never
  // surface another list's items (the push-field picker reuses one component
  // instance across listSlugs). Track which list produced the last settled
  // payload and suppress laggy data that crossed a list boundary.
  const lastSettledListIdRef = useRef<string | null>(null);
  const isLaggy = isLoading && data !== undefined;
  // Intentional render-phase ref write: it is idempotent (same value on every
  // render for a given SWR state, so StrictMode double-render is safe) and
  // must land before this render returns — moving it into a useEffect would
  // reintroduce a one-frame flash of the wrong list's items.
  if (key !== null && data !== undefined && !isLaggy) {
    lastSettledListIdRef.current = options.listId;
  }
  const items =
    key !== null &&
    data !== undefined &&
    (!isLaggy || lastSettledListIdRef.current === options.listId)
      ? data.items ?? EMPTY_ITEMS
      : EMPTY_ITEMS;

  return {
    items,
    isLoading,
    error: error as Error | undefined,
    /** Bound revalidate for the current key (e.g. after creating an item). */
    mutate,
  };
}
